// src/constants/wordOpenClose.ts
// 決定C（IDEAS.md 2026-07-01「決定C確定＋14語連結配置＋戦略2案」）：
// カタルタ語の「前後の開き」分類。
//   前：お題/前の流れ無しで、いきなりめくって使えるか？ 開＝Yes（使える）／閉＝No（要prior）
//   後：その後、話を開いたまま続けられる（次へ渡せる）か／畳む・締めるに閉じるか？ 開＝続く／閉＝締める
//
// 2026-07-11：軌跡マップ（稜線レイアウト）の傾きの意味づけを、便宜上の「語の重さ」から
// この決定Cベースに差し替え（CEO指示・Kazuto訂正で下り＝後開／上り＝後閉に確定）。
//
// 【2026-07-11追記・度合い化】IDEAS.mdには以下の重要な補足がある：
//   - 「程度はグラデーション、2×2は扱いやすい粗い区切り」（7/1）＝開閉は本来2値でなく連続的な度合い。
//   - 「カタルタ語の隠れた本性＝前後の語を縛りすぎない（縛る語ほど汎用性を損なう）」（6/30-7/1）。
//   - 「全部後開きだと展開ベクトルが偏る→後閉じを数枚混ぜ幅を出す。54枚の確率論で乱れは薄まる」（7/1）
//     ＝閉じ語は意図的に少数だけ混ぜる“調味料”であり、全体のベースは開き寄り。
// これを受けて、front/backを真偽値でなく「開き度合い」（0〜1）に変更した。デフォルトは
// 開き寄り（0.85）とし、決定C確定語・要約結論語・明確な比較語など「閉じている」と言い切れる
// 語だけを低い値にする。稜線の角度もこの度合いに連続的に比例させ、硬い二択ゾーンをやめた。
//
// 【2026-07-13追記・前後軸の意味づけ再定義】Kazutoとの議論で、前後軸の本来の意味は
// 「文脈の有無」ではないと判明：
//   前＝「受け入れ度合い」＝前にどんな内容が来ても自然に乗れるか（文脈の有無でなく柔軟性）。
//     例：「たとえば」はどんな前振りにも乗る＝開。「では」は前振りを選ぶ＝閉。
//   後＝「発散/収束」＝話を複数方向に開いたまま渡せるか、1点にまとめてしまうか。
//     （前回7/11の「後開＝すんなり続く／後閉＝要prior・力試し」の意味づけと矛盾しない）
// これに合わせて前軸の1次判定パターンを見直した：それから・それに・しかも・さらに・また・
// そのうえ・ところで＝単純な継続・話題転換語は「前をほぼ選ばない」ため開き寄りへ移動。
// したがって・というわけで・そういうわけで＝「だから」と同格の因果の強さのため強い閉じ寄りへ格上げ。
// だから・とにかく・さもなければは既にDECIDEDにあるため重複を削除（挙動は変えていない）。

export type OpenClose = { front: number; back: number }; // 0=閉に寄る〜1=開に寄る（度合い）

// 全体としてカタルタ語は開き寄り（IDEAS.md「縛る語ほど汎用性を損なう」）。
// 未分類語のデフォルト値はここから始める。
const DEFAULT_OPENNESS = 0.85;

// 決定C確定済み・14語（IDEAS.md 2026-07-01・Kazuto承認）。度合いは代表値として
// 開＝0.9、閉＝0.15に写像（真偽値だった確定情報を度合いスケールに載せ替えただけで、
// 分類そのものは変えていない）。
const DECIDED: Record<string, OpenClose> = {
  "もし": { front: 0.9, back: 0.9 },
  "おそらく": { front: 0.9, back: 0.9 },
  "偶然にも": { front: 0.9, back: 0.9 },
  "そして": { front: 0.9, back: 0.9 },
  "たとえば": { front: 0.9, back: 0.9 },
  "同様に": { front: 0.15, back: 0.9 },
  "むしろ": { front: 0.15, back: 0.9 },
  "一方": { front: 0.15, back: 0.9 },
  "対照的に": { front: 0.15, back: 0.9 },
  "さもなければ": { front: 0.15, back: 0.9 },
  "だから": { front: 0.15, back: 0.9 },
  "とにかく": { front: 0.15, back: 0.9 },
  "つまり": { front: 0.15, back: 0.15 },
  "結局": { front: 0.15, back: 0.15 },
};

// 未分類語の1次判定パターン（2026-07-11・叩き台・要Kazutoレビュー）。
// 「閉じ＝少数の意図的な調味料」という設計思想に合わせ、強く閉じている語だけを低くし、
// 残りはデフォルト（開き寄り）のまま触らない、という保守的な運用にしている。

// 前・強く閉じ寄り（比較・対比＝何かと比べる前提が必須。要約・結論＝先行内容の総括が必須。
// したがって・というわけで・そういうわけでは「だから」相当の因果の強さとして2026-07-13にこちらへ統合）
const FRONT_STRONG_CLOSED = [
  "同様に", "むしろ", "一方", "一方で", "いっぽう", "対照的に",
  "つまり", "結局", "ようするに", "要するに", "いいかえると", "言い換えると", "いってみれば",
  "したがって", "というわけで", "そういうわけで",
];

// 前・やや閉じ寄り（逆接・限定的な因果＝先行文脈があるとより自然だが、無くても致命的ではない）
// 2026-07-13：それから・それに・しかも・さらに・また・そのうえ・ところでは「受け入れ度合い」の
// 観点では前をほぼ選ばないため削除（→デフォルト0.85の開き寄りへ）。だから・とにかく・さもなければは
// 既にDECIDEDにあるため重複削除。
const FRONT_MILD_CLOSED = [
  "とはいえ", "それでも", "にもかかわらず", "そのくせ", "けれども",
  "だからもし", "なぜなら", "というのも", "だからこそ", "しかしなぜ",
  "せっかく", "せっかくだから",
  "しかし", "ところが", "でも", "それにしても",
  "そこで", "すると",
];

// 後・強く閉じ寄り（話を畳む・締める・要約結論。これ以外は基本的に開＝続けられる、のまま）
const BACK_STRONG_CLOSED = [
  "つまり", "結局", "ようするに", "要するに", "いいかえると", "言い換えると", "しかしなぜ",
];

/** 語から「前後の開き度合い」（0〜1）を判定する。決定C確定語はそれを優先する。 */
export function classifyWord(word: string): OpenClose {
  const decided = DECIDED[word];
  if (decided) return decided;

  let front = DEFAULT_OPENNESS;
  if (FRONT_STRONG_CLOSED.includes(word)) front = 0.2;
  else if (FRONT_MILD_CLOSED.includes(word)) front = 0.45;

  const back = BACK_STRONG_CLOSED.includes(word) ? 0.2 : DEFAULT_OPENNESS;

  return { front, back };
}
